import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import {
  reduxForm,
} from 'redux-form';
import {
  Badge,
  Container,
} from 'reactstrap';
import setPropsAsInitialValues from '../../../helpers/setPropsAsInitialValues';
import DataTable from '../../../shared/components/DataTable';
import TooltipItem from '../../../shared/components/TooltipItem';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import {
  fetchMy,
} from '../../../redux/actions/assistanceActions';
import {
  dateMMFormat,
  dateDDFormat,
} from '../../../helpers/dateFormat';
import {
  createErrorMessage,
} from '../../../helpers/notification';

class AssistanceContainer extends PureComponent {
  static propTypes = {
    subjectPeriodId: PropTypes.number.isRequired,
    loadAssistance: PropTypes.bool.isRequired,
  }

  constructor(props) {
    super(props);

    this.state = {
      page: 1,
      pageSize: 25,
      totalItemsCount: 0,
      loading: true,
      loadingAssists: false,
      items: [],
      assists: [],
      studentAssists: [],
      filters: { id: 10 },
      showForm: false,
      students: [],
      currentStudentId: 0,
    };

    this.columns = ({ assists, studentAssists, canEditAttendances = true }) => {
      const cols = [];

      const groupByAssistance = assists.map(({ id, date, reference }) => ({ assistanceId: id, date, reference }));

      const assistanceByStudents = studentAssists
        .map(({ assistanceId, studentId, present }) => ({ assistanceId, studentId, present }));
      const assistanceLength = groupByAssistance.length;
      const attendancePerc = [];

      studentAssists
        .map(({ studentId, present }) => ({ studentId, present }))
        .reduce((res, { studentId, present }) => {
          if (!res[studentId]) {
            res[studentId] = { studentId, ok: 0 };
            attendancePerc.push(res[studentId]);
          }

          res[studentId].ok += present ? 1 : 0;

          return res;
        }, {});

      const assistanceHeaders = !canEditAttendances
        ? []
        : groupByAssistance.map(a => ({
          key: `assistance${a.assistanceId}`,
          name: (
            <div style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
              <div>{dateDDFormat(a.date)}{a.reference ? `-${a.reference}` : ''}</div>
              <TooltipItem
                key={`tti-id-${a.assistanceId}`}
                id={a.assistanceId}
                value={dateMMFormat(a.date)}
                label={a.reference}
                color="secondary"
              />
            </div>
          ),
          width: '75px',
          className: 'text-center',
          format: ({ studentId }) => {
            const assistanceByStudent = assistanceByStudents
              .find(as => as.assistanceId === a.assistanceId && as.studentId === studentId);

            if (!assistanceByStudent || assistanceByStudent.present === null) {
              return <span role="img" aria-label="Sin Asistencia">​➖</span>;
            }

            if (assistanceByStudent.present === false) {
              return (
                <span role="img" aria-label="Ausente">​❌</span>
              );
            }

            return (
              <span role="img" aria-label="Presente">✔️</span>
            );
          },
        }));

      return [
        ...cols,
        ...[
          {
            key: 'assistPerc',
            name: '% Asistencia',
            editable: false,
            width: '110px',
            className: 'text-center',
            inputStyle: () => this.inputStyle,
            format: ({ studentId: id, assistPerc = 0 }) => (
              <Badge
                color="primary"
                pill
                style={{
                  textAlign: 'center',
                  fontWeight: 'normal',
                }}
              >
                {
                  canEditAttendances
                    ? assistPerc
                    : attendancePerc.filter(({ studentId }) => id === studentId)
                      .map(({ ok }) => Math.round((ok / assistanceLength) * 100))[0]
                    || 0
                }
              </Badge>
            ),
          },
        ],
        ...assistanceHeaders,
      ];
    };
  }

  componentDidMount() {
    const {
      loadAssistance,
    } = this.props;

    if (!loadAssistance) {
      this.setState({
        loading: false,
      });
      return;
    }

    this.refreshTable();
  }

  componentDidUpdate(prevProps) {
    const { loadAssistance } = this.props;

    if (loadAssistance === prevProps.loadAssistance) {
      return;
    }

    if (loadAssistance) {
      this.refreshTable();
    }
  }

  refreshTable = (criteria = {}) => {
    const {
      page = 1, pageSize = 25, totalItemsCount = 0, filters = {},
    } = criteria;

    const state = {
      filters: filters || { logContent: [] },
      page,
      pageSize,
      totalItemsCount,
    };

    this.fetchTable({ ...state });
  }

  fetchTable = ({ page, pageSize, filters }) => {
    const {
      subjectPeriodId,
    } = this.props;

    this.setState({
      loading: true,
      page,
      pageSize,
      filters: filters || {},
    });

    fetchMy({
      subjectPeriodId,
      pageInfo: {
        page,
        pageSize,
        filters,
      },
      onSuccess: ({ pageInfo, assists, studentAssists }) => {
        const { items, totalItemsCount } = pageInfo;
        this.setState({
          loading: false,
          items,
          totalItemsCount,
          assists,
          studentAssists,
        });
      },
      onError: () => {
        this.setState({ items: [], loading: false });
        createErrorMessage('Error al intentar cargar la lista de materias por periodo');
      },
    });
  }

  handleHideForm = () => {
    this.setState({
      showForm: false,
    });

    this.refreshTable();
  };

  onChangePage = (page) => {
    if (page) {
      const { pageSize, filters } = this.state;
      this.refreshTable({ page, pageSize, filters });
    }
  };

  onDataTableSubmit = (values) => {
    if (values) {
      const { pageSize } = this.state;
      this.refreshTable({ page: 1, pageSize, values });
    }
  };

  onDataTableClearFilters = () => {
    const { pageSize, filters } = this.state;
    const { autoHideSearch } = filters;
    this.refreshTable({ page: 1, pageSize, filters: { autoHideSearch } });
  };

  nextPage = () => {
    const { students = [], currentStudentId } = this.state;

    const index = students.findIndex(s => s.studentId === currentStudentId);
    const newIndex = index + 1 === students.length ? 0 : index + 1;

    const student = students[newIndex];

    this.setState({ currentStudentId: student.studentId });
  };

  previousPage = () => {
    const { students = [], currentStudentId } = this.state;

    const index = students.findIndex(s => s.studentId === currentStudentId);
    const { studentId } = students[index === 0 ? students.length - 1 : index - 1];

    this.setState({ currentStudentId: studentId });
  };

  assistanceButtons = ({
    assistanceId,
    currentStudentId,
    assistanceValue,
    onPreviousPage,
    onNextPage,
    onPresent,
    onAbsent,
    pointerStudent,
  }) => {
    const { loadingAssists } = this.state;
    if (loadingAssists) {
      return <LoadingSpinner />;
    }

    return (
      <th>
        <div
          className="form__form-group-field"
          style={{ columnGap: '0.5rem', justifyContent: 'space-between' }}
        >
          <button
            key={`button-arrow-left-${assistanceId}-${currentStudentId}`}
            type="button"
            className="action-button"
            onClick={onPreviousPage}
          >
            <span
              key={`span-arrow-left${assistanceId}-${currentStudentId}`}
              title="Anterior"
              className="fa fa-arrow-circle-left fa-2x"
            />
          </button>
          <div style={{ display: 'grid', fontSize: '1.1rem', justifyItems: 'center' }}>
            <div>{pointerStudent}</div>
            <span
              className={`badge badge-${assistanceValue.color}`}
              style={{ textAlign: 'center' }}
            >
              {assistanceValue.description}
            </span>
            <div>
              <button
                key={`button-ok-${assistanceId}-${currentStudentId}`}
                type="button"
                className="action-button"
                onClick={() => onPresent({ assistanceId, studentId: currentStudentId })}
              >
                <span
                  key={`span-ok-${assistanceId}-${currentStudentId}`}
                  title="Presente"
                  className="fa fa-check fa-2x"
                />
              </button>
              <button
                key={`button-no-${assistanceId}-${currentStudentId}`}
                type="button"
                className="action-button"
                onClick={() => onAbsent({ assistanceId, studentId: currentStudentId })}
              >
                <span
                  key={`span-no-${assistanceId}-${currentStudentId}`}
                  title="Ausente"
                  className="fa fa-times fa-2x"
                />
              </button>
            </div>
          </div>
          <button
            key={`button-arrow-right-${assistanceId}-${currentStudentId}`}
            type="button"
            className="action-button"
            onClick={onNextPage}
          >
            <span
              key={`span-arrow-right-${assistanceId}-${currentStudentId}`}
              title="Anterior"
              className="fa fa-arrow-circle-right fa-2x"
            />
          </button>
        </div>
      </th>
    );
  };

  dataTable = () => {
    const {
      showForm,
      items,
      page,
      pageSize,
      totalItemsCount,
      filters,
      assists,
      studentAssists,
    } = this.state;

    if (showForm) {
      return null;
    }

    return (
      <DataTable
        key="tblAssists"
        title="Listado de todas las asistencias"
        columns={this.columns({ assists, studentAssists })}
        items={items}
        onChangePage={this.onChangePage}
        totalItemsCount={totalItemsCount}
        onSubmit={this.onDataTableSubmit}
        onClearFilters={this.onDataTableClearFilters}
        // getGridActions={(key, row) => this.getGridActions(key, row)}
        itemsToShow={items.length}
        page={page}
        pageSize={pageSize}
        autoHideSearch={filters.autoHideSearch}
        initialValues={filters}
        enableReinitialize
      />
    );
  };

  render() {
    const {
      loading,
    } = this.state;

    if (loading) {
      return <LoadingSpinner />;
    }

    return (
      <Container>
        {this.dataTable()}
      </Container>
    );
  }
}

const ReduxAssistanceContainer = reduxForm({
  form: 'AssistanceContainer',
  enableReinitialize: true,
})(AssistanceContainer);

export default setPropsAsInitialValues(ReduxAssistanceContainer);
